'use client';

/**
 * DetailPage hero header — Phase A2.
 *
 * Anatomy:
 *   ← Back row     [presence stack] [copy-link]
 *   eyebrow / breadcrumb
 *   TITLE                                    [actions]
 *   [status pills]                           [secondary actions]
 *   [optional urgency banner — e.g. SIRS countdown]
 *
 * The hero takes lots of slots but renders them all under one
 * predictable wrapper so every detail page in the platform has the
 * same visual rhythm.
 */

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';
import { BannerSlot } from './banner';
import { SectionTabsSlot } from '../sub-page-tabs/sub-page-tabs';

export interface DetailPageHeaderProps {
  /**
   * Optional back-link href. Omit to hide the back row. The header
   * renders a plain anchor by default; pass `backLinkRender` to use
   * a framework router (e.g. Next.js `<Link>`) — see
   * `apps/web/src/components/dashboard-shell-v2.tsx` for an example.
   */
  backHref?: string;
  /** Back-link label (e.g. "Clients", "Incidents"). */
  backLabel?: string;
  /**
   * Render-prop for the back link. Defaults to a plain anchor so the
   * UI package stays framework-agnostic. Web app should wire to a
   * Next.js `<Link>` to keep prefetch + soft-navigation behaviour.
   */
  backLinkRender?: (props: {
    href: string;
    className: string;
    children: React.ReactNode;
  }) => React.ReactNode;
  /** Additional serialisable classes for the back link. */
  backLinkClassName?: string;
  /** Eyebrow text above the title (e.g. "Incident number ABC123", "Care plan"). */
  eyebrow?: React.ReactNode;
  /** Page title — should be the single most-important identifier. */
  title: string;
  /** Status pills row below the title (chips, badges). */
  statusPills?: React.ReactNode;
  /** Structured label/value metadata, usually a KeyFacts description list. */
  meta?: React.ReactNode;
  /** Primary + secondary action buttons (right side of the title row). */
  actions?: React.ReactNode;
  /** Optional inline banner (e.g. SIRS countdown, deadline warning). */
  banner?: React.ReactNode;
  /** Right-rail slot for presence / collaboration indicators. */
  presence?: React.ReactNode;
  /** Right-rail slot for copy-link / share controls. */
  share?: React.ReactNode;
  /** Additional header layout classes. */
  className?: string;
}

export function DetailPageHeader({
  backHref,
  backLabel,
  backLinkRender,
  backLinkClassName,
  eyebrow,
  title,
  statusPills,
  meta,
  actions,
  banner,
  presence,
  share,
  className,
}: DetailPageHeaderProps) {
  const backLinkClass = cn(
    'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-control px-2.5 py-2 text-sm text-text-muted hover:bg-surface-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
    backLinkClassName,
  );
  const backLinkChildren = (
    <>
      <ArrowLeft className="h-3 w-3" aria-hidden="true" />
      {backLabel ?? 'Back'}
    </>
  );
  return (
    <header className={cn('flex min-w-0 flex-col gap-4 pb-4', className)} data-detail-page-header>
      {/* Top row: back link + presence/share */}
      {(backHref || presence || share) && (
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {backHref ? (
            backLinkRender ? (
              backLinkRender({
                href: backHref,
                className: backLinkClass,
                children: backLinkChildren,
              })
            ) : (
              <a href={backHref} className={backLinkClass}>
                {backLinkChildren}
              </a>
            )
          ) : (
            <span />
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            {presence}
            {share}
          </div>
        </div>
      )}

      {/* Title + actions row */}
      <div className="flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
        <div className="min-w-0 flex-[1_1_16rem]">
          <PageTitle className="break-normal [overflow-wrap:break-word]">{title}</PageTitle>
          {eyebrow && (
            <p className="mt-2 break-normal font-body text-caption font-normal text-text-muted [overflow-wrap:break-word]">
              {eyebrow}
            </p>
          )}
          {meta != null && (
            <div className="mt-4 min-w-0 font-body text-body font-medium tabular-nums text-text-heading [&_dt]:text-caption [&_dt]:font-normal [&_dt]:text-text-muted [&_dd]:mt-1">
              {meta}
            </div>
          )}
          {statusPills && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">{statusPills}</div>
          )}
        </div>
        {actions && (
          <div className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center justify-start gap-2 [&>div]:flex [&>div]:min-w-0 [&>div]:flex-wrap [&>div]:gap-2 [&_button]:min-w-fit [&_a]:min-w-fit [&_button]:whitespace-nowrap [&_button]:shrink-0 [&_a]:whitespace-nowrap [&_a]:shrink-0">
            {actions}
          </div>
        )}
      </div>

      {/* Optional inline banner (urgency / warning / deadline) */}
      <BannerSlot record={banner} />
      <SectionTabsSlot />
    </header>
  );
}
